<script setup lang="ts">
import Popover from 'primevue/popover';
import Tree from 'primevue/tree';
import { useProjectStore } from '@/store';
import { useAlerts } from '@/utils/helpers/alerts';
import type { TreeNode } from 'primevue/treenode';

const route = useRoute();

const popover = ref();
const messages = useAlerts();
const projectTree = ref<TreeNode[]>([]);
const isLoading = ref(false);
const showToggleButton = ref(false);

const projectStore = useProjectStore();

const togglePopover = (event: MouseEvent) => {
  popover.value?.toggle(event);
};
const hidePopover = () => {
  popover.value?.hide();
};

const fetchProjectTree = async (projectId: string) => {
  try {
    isLoading.value = true;
    projectTree.value = await projectStore.projectTree(projectId);
    if (projectTree.value[0]?.children?.length) {
      showToggleButton.value = true;
    }
  } catch (error) {
    messages.error(error);
  } finally {
    isLoading.value = false;
  }
};

watch(
  () => route.params.projectId as string,
  async (newProjectId) => {
    if (!newProjectId) {
      return;
    }
    hidePopover();
    showToggleButton.value = false;
    await fetchProjectTree(newProjectId);
  },
  { immediate: true },
);
</script>

<template>
  <UiButton
    v-if="showToggleButton"
    variant="flat"
    class="notification-button"
    @click="togglePopover"
  >
    <UiIcon name="FolderTree" class="icon-size-sm" />
  </UiButton>

  <Popover
    ref="popover"
    :pt="{
      content: { style: { padding: '0' } },
    }"
  >
    <div class="tree-container">
      <Tree :value="projectTree" :loading="isLoading">
        <template #active="slotProps">
          <span class="active tree-label">{{ slotProps.node.label }}</span>
        </template>
        <template #link="slotProps">
          <RouterLink
            :to="{
              name: 'ProjectPage',
              params: { projectId: slotProps.node.key },
            }"
          >
            <span class="link tree-label">{{ slotProps.node.label }}</span>
          </RouterLink>
        </template>
      </Tree>
    </div>
  </Popover>
</template>

<style lang="scss" scoped>
.tree-container {
  width: 300px;
}
.tree-label {
  font-size: var(--font-size-14);
  display: block;
  width: 230px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.link {
  font-weight: var(--font-weight-bold);
}
.active {
  font-style: italic;
}
:deep(.p-tree) {
  padding: var(--space-4);
}
</style>
